import { FunctionComponent, Fragment } from 'preact';
import { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';
import type { StreamProvider } from '@vgtv/api-client';
import { getSubscriptionUrl } from '@vgtv/paywall-utils';
import type { Asset } from '@schibsted-svp/svp-api-types';
import cn from 'classnames';

import { usePaywallState } from '../../hooks/usePaywallState';
import { LoginButton } from '../LoginButton/LoginButton';
import { SubscribeButton } from '../SubscribeButton/SubscribeButton';
import { SalesPoster } from '../SalesPoster/SalesPoster';
import { usePaywallHandlers } from '../../hooks/usePaywallHandlers';
import { useProvider } from '../../hooks/useProvider';
import { usePaywall } from '../../hooks/PaywallProvider';
import { device } from '../../../../utils/device';
import { Button, ButtonProps } from '../Button/Button';
import { useTranslation } from '../../../../utils/useTranslation';
import ns from '../../translations';
import { getSubscriptionLabel } from '../../../../utils/subscription';
import {
  isSportAccessLevel,
  isSubscriptionAccessLevel,
} from '../../../../utils/player/asset';
import { useAccessDefinitions } from '../../hooks/AccessDefinitionsProvider';

interface PaywallButtonsProps extends Pick<ButtonProps, 'size' | 'color'> {
  asset: Asset;
  accessLevel: ProviderAccessMap[StreamProvider] | 'login' | undefined;
  variant?: 'default' | 'podcast';
}

export const PaywallButtons: FunctionComponent<PaywallButtonsProps> = ({
  size,
  asset,
  accessLevel,
  variant = 'default',
}) => {
  const { hasAccess, isLoggedIn } = usePaywallState(accessLevel);
  const { configProvider } = useProvider();
  const { utmParameters, getSubscriptionUrl: getSubscriptionUrlOverride } =
    usePaywall();
  const { t } = useTranslation(ns);
  const { accessDefinitions } = useAccessDefinitions();

  const overriddenUrl = getSubscriptionUrlOverride?.(asset);
  const defaultUrl = accessLevel
    ? (getSubscriptionUrl({
        // TODO: refine this later
        asset: asset as any,
        access: accessLevel as Exclude<typeof accessLevel, 'total'>,
        utmParams: utmParameters,
        provider: configProvider,
        device,
      }) ?? undefined)
    : undefined;
  const subscriptionUrl = overriddenUrl ?? defaultUrl;

  const {
    paywallType,
    isSalesPosterOpen,
    handlePosterLoadFailure,
    handlePosterCloseClick,
    handleSubscribeClick,
  } = usePaywallHandlers(subscriptionUrl, {
    isUrlOverridden: Boolean(overriddenUrl),
  });

  if (hasAccess) {
    return null;
  }

  const displaySubscriptionIncentive = isSubscriptionAccessLevel(
    configProvider,
    accessLevel
  );
  const subscriptionLabel =
    variant === 'podcast'
      ? t('Subscribe to listen', {}, { noJSX: true })
      : t(
          'Watch with subscription',
          {
            subscription: getSubscriptionLabel(accessDefinitions, accessLevel),
          },
          { noJSX: true }
        );

  return (
    <div
      className={cn('vgp-paywall-buttons', {
        'vgp-paywall-buttons-sport': isSportAccessLevel(
          configProvider,
          accessLevel
        ),
      })}
    >
      {!isLoggedIn && <LoginButton size={size} color="video" />}
      {displaySubscriptionIncentive && (
        <Fragment>
          {paywallType === 'disabled' ? (
            <Button disabled>{subscriptionLabel}</Button>
          ) : (
            <SubscribeButton
              accessLevel={accessLevel}
              size={size}
              color="primary"
              labelOverride={subscriptionLabel}
              onSubscribeClick={handleSubscribeClick}
              subscriptionUrl={subscriptionUrl}
            />
          )}
          {paywallType === 'popup' && (
            <SalesPoster
              asset={asset}
              accessLevel={accessLevel}
              isOpen={isSalesPosterOpen}
              onCloseClick={handlePosterCloseClick}
              onLoadFailure={handlePosterLoadFailure}
            />
          )}
        </Fragment>
      )}
    </div>
  );
};
